import React, { useState, useMemo } from 'react';
import useEscapeKey from '../../../hooks/useEscapeKey';
import Modal from '../../../components/Modal';
import { getEmployeeDisplayName } from '../utils/helpers';
import { API_BASE } from '../utils/constants';

export default function ReportsTab({ reports, loading, user }) {
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedEmployeeHistory, setSelectedEmployeeHistory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [hoveredBadge, setHoveredBadge] = useState(null);
  const [hoveredCalendarBadge, setHoveredCalendarBadge] = useState(null);
  const [hoveredAnniversaryBadge, setHoveredAnniversaryBadge] = useState(null);
  const [calculationModalOpen, setCalculationModalOpen] = useState(false);
  const [selectedEmployeeCalculation, setSelectedEmployeeCalculation] = useState(null);
  const [calculationLoading, setCalculationLoading] = useState(false);
  const [calculationData, setCalculationData] = useState(null);

  // Handle escape key to close modal
  useEscapeKey(() => {
    if (historyModalOpen) {
      setHistoryModalOpen(false);
    }
    if (calculationModalOpen) {
      setCalculationModalOpen(false);
    }
  });

  // Handle database export
  const handleExportDatabase = async () => {
    setExportLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/export_database`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user?.email || ''
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to export database');
      }

      const result = await response.json();
      
      if (result.success) {
        // Create and download each JSON file - all tables from export
        const fileEntries = Object.entries(result.data || {});

        fileEntries.forEach(([fileName, data]) => {
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        });

        // Build summary for success message
        const counts = [
          `employees.json (${result.total_employees || 0} employees)`,
          `policies.json (${result.total_policies || 0} policies)`,
          `pto_requests.json (${result.total_requests || 0} requests)`,
          `balance_adjustments.json (${result.total_adjustments || 0} adjustments)`,
          `onboarding.json (${result.total_onboarding_items || 0} items)`,
          `probation_policies.json (${result.total_probation_policies || 0} policies)`
        ];
        alert(`Database exported successfully!\n\nFiles downloaded:\n- ${counts.join('\n- ')}\n\nExport timestamp: ${new Date(result.export_timestamp).toLocaleString()}`);
      } else {
        throw new Error(result.error || 'Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert(`Export failed: ${error.message}`);
    } finally {
      setExportLoading(false);
    }
  };

  // Function to open history modal
  const openHistoryModal = (employee) => {
    setSelectedEmployeeHistory(employee);
    setHistoryModalOpen(true);
  };

  // Function to fetch and open calculation breakdown modal
  const openCalculationModal = async (employee) => {
    setSelectedEmployeeCalculation(employee);
    setCalculationModalOpen(true);
    setCalculationLoading(true);
    setCalculationData(null);

    try {
      const response = await fetch(`${API_BASE}/api/admin/test_pto_accrual/${encodeURIComponent(employee.email)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user?.email || ''
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch calculation breakdown');
      }

      const result = await response.json();
      setCalculationData(result);
    } catch (error) {
      console.error('Calculation fetch error:', error);
      alert(`Failed to fetch calculation: ${error.message}`);
    } finally {
      setCalculationLoading(false);
    }
  };

  // Fuzzy search function
  const fuzzyMatch = (searchTerm, text) => {
    if (!searchTerm || !text) return true;
    
    const search = searchTerm.toLowerCase();
    const target = text.toLowerCase();
    
    // Direct substring match (most relevant)
    if (target.includes(search)) return true;
    
    // Fuzzy character sequence match
    let searchIndex = 0;
    for (let i = 0; i < target.length && searchIndex < search.length; i++) {
      if (target[i] === search[searchIndex]) {
        searchIndex++;
      }
    }
    return searchIndex === search.length;
  };

  // Filter reports based on search term
  const filteredReports = useMemo(() => {
    if (!searchTerm.trim()) return reports;
    
    return reports.filter(rep => {
      const fullName = getEmployeeDisplayName({ 
        first_name: rep.first_name, 
        last_name: rep.last_name, 
        email: rep.email 
      });
      
      const searchableFields = [
        rep.email || '',
        rep.first_name || '',
        rep.last_name || '',
        fullName,
        rep.title || ''
      ];
      
      return searchableFields.some(field => fuzzyMatch(searchTerm, field));
    });
  }, [reports, searchTerm]);

  // Calculate macro insights for executive dashboard
  const macroInsights = useMemo(() => {
    if (!reports || reports.length === 0) return null;

    // Filter out reports with no data
    const validReports = reports.filter(rep => 
      rep.balance && 
      typeof rep.balance.total === 'number' && 
      typeof rep.balance.remaining === 'number'
    );

    // Highest utilization (most % days off)
    const highestUtilization = validReports
      .filter(rep => rep.utilization_pct != null && rep.utilization_pct > 0)
      .sort((a, b) => (b.utilization_pct || 0) - (a.utilization_pct || 0))
      .slice(0, 3);

    // Lowest utilization (least % days off)
    const lowestUtilization = validReports
      .filter(rep => rep.utilization_pct != null)
      .sort((a, b) => (a.utilization_pct || 0) - (b.utilization_pct || 0))
      .slice(0, 3);

    // Most negative balance (concerning - overused PTO)
    const mostNegativeBalance = validReports
      .filter(rep => (rep.balance.remaining || 0) < 0)
      .sort((a, b) => (a.balance.remaining || 0) - (b.balance.remaining || 0))
      .slice(0, 3);

    // Highest positive balance (most unused PTO)
    const highestPositiveBalance = validReports
      .filter(rep => (rep.balance.remaining || 0) > 0)
      .sort((a, b) => (b.balance.remaining || 0) - (a.balance.remaining || 0))
      .slice(0, 3);

    // Company averages
    const totalEmployees = validReports.length;
    const avgUtilization = validReports
      .filter(rep => rep.utilization_pct != null)
      .reduce((sum, rep) => sum + (rep.utilization_pct || 0), 0) / 
      validReports.filter(rep => rep.utilization_pct != null).length;
    
    const avgBalance = validReports
      .reduce((sum, rep) => sum + (rep.balance.remaining || 0), 0) / totalEmployees;

    const negativeBalanceCount = validReports
      .filter(rep => (rep.balance.remaining || 0) < 0).length;

    return {
      highestUtilization,
      lowestUtilization,
      mostNegativeBalance,
      highestPositiveBalance,
      avgUtilization: avgUtilization || 0,
      avgBalance: avgBalance || 0,
      totalEmployees,
      negativeBalanceCount
    };
  }, [reports]);

  // Function to format dates properly (avoiding timezone issues)
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      // Handle both date strings and datetime strings
      if (dateStr.includes('T')) {
        // This is a datetime string, format it normally
        return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      } else {
        // This is a date string, parse as local date to avoid timezone issues
        const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
        const date = new Date(year, month - 1, day); // month is 0-indexed
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      }
    } catch {
      return dateStr;
    }
  };

  // Helper function to get utilization color
  const getUtilizationColor = (pct) => {
    if (pct == null) return 'var(--symbolica-muted)';
    if (pct < 20) return '#059669'; // Green
    if (pct < 40) return '#0891b2'; // Teal  
    if (pct < 60) return '#ea580c'; // Orange
    return '#dc2626'; // Red
  };

  // Helper function to get status badge style
  const getStatusBadge = (status) => {
    const baseStyle = {
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    };

    switch (status?.toLowerCase()) {
      case 'approved':
        return { ...baseStyle, background: '#dcfce7', color: '#166534' };
      case 'pending':
        return { ...baseStyle, background: '#fef3c7', color: '#92400e' };
      case 'rejected':
      case 'denied':
        return { ...baseStyle, background: '#fef2f2', color: '#dc2626' };
      default:
        return { ...baseStyle, background: '#F0EDE6', color: 'var(--symbolica-black)' };
    }
  };

  // Helper function to create progress bar
  const ProgressBar = ({ value, max, color = 'var(--symbolica-black)', height = 6 }) => {
    const percentage = max > 0 ? Math.min((value / max) * 100, 100) : 0;
    return (
      <div style={{
        width: '100%',
        height: `${height}px`,
        backgroundColor: '#F0EDE6',
        borderRadius: '3px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${percentage}%`,
          height: '100%',
          backgroundColor: color,
          transition: 'width 0.3s ease'
        }} />
      </div>
    );
  };

  // Helper function to create metric card
  const MetricCard = ({ icon, label, value, subtitle, color = 'var(--symbolica-black)', progress }) => (
    <div style={{
      background: 'var(--symbolica-card)',
      border: `1px solid ${color}20`,
      borderRadius: '8px',
      padding: '12px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: color
      }} />
      <div style={{ fontSize: '18px', marginBottom: '4px' }}>{icon}</div>
      <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--symbolica-black)', marginBottom: '2px' }}>
        {value}
      </div>
      <div style={{ fontSize: '11px', color: 'var(--symbolica-muted)', fontWeight: '500', marginBottom: '6px' }}>
        {label}
      </div>
      {progress && (
        <ProgressBar value={progress.value} max={progress.max} color={color} height={4} />
      )}
      {subtitle && (
        <div style={{ fontSize: '10px', color: 'var(--symbolica-muted)', marginTop: '4px' }}>
          {subtitle}
        </div>
      )}
    </div>
  );

  // Helper function to create insight card for macro report
  const InsightCard = ({ title, employees, metric, isWarning = false }) => (
    <div style={{
      background: 'var(--symbolica-card)',
      border: '1px solid var(--symbolica-warm-border)',
      borderRadius: '10px',
      padding: '18px',
    }}>
      <h4 style={{
        margin: '0 0 14px',
        fontSize: '13px',
        fontWeight: '600',
        color: 'var(--symbolica-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}>
        {title}
      </h4>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {employees.length === 0 ? (
          <div style={{
            padding: '10px',
            color: '#B0AFA8',
            fontSize: '13px',
          }}>
            {isWarning ? 'No concerning cases found' : 'No data available'}
          </div>
        ) : (
          employees.map((emp) => {
            const name = getEmployeeDisplayName({
              first_name: emp.first_name,
              last_name: emp.last_name,
              email: emp.email
            });
            return (
              <div key={emp.email} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 10px',
                background: '#F6F4F0',
                borderRadius: '6px',
              }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--symbolica-black)' }}>
                    {name}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--symbolica-muted)' }}>
                    {emp.title || '\u2014'}
                  </div>
                </div>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: isWarning ? '#B91C1C' : 'var(--symbolica-black)',
                }}>
                  {metric(emp)}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  if (!user?.is_admin) {
    return (
      <div style={{ textAlign: 'center', padding: '32px', color: 'var(--symbolica-muted)' }}>
        Access denied. Admin privileges required.
      </div>
    );
  }

  return (
    <>
      <div style={{ 
        background: '#EFECE0',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '32px',
        color: 'var(--symbolica-black)'
      }}>
        <h1 className="content-title" style={{ margin: '0 0 8px 0' }}>
          Time-Off Reports Dashboard
        </h1>
        <p style={{ fontSize: '1rem', opacity: 0.9, margin: 0 }}>
          Comprehensive view of team time off balances, usage, and upcoming requests
        </p>
      </div>



      {/* Executive Macro Report */}
      {macroInsights && (
        <div style={{ marginBottom: '32px' }}>
          {/* Company Overview Stats */}
          <div style={{
            background: '#F6F4F0',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            color: 'var(--symbolica-black)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
                Company Overview
              </h2>
              <div style={{ fontSize: '13px', opacity: 0.8 }}>
                {macroInsights.totalEmployees} employees tracked
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: 4 }}>
                  {macroInsights.avgUtilization.toFixed(1)}%
                </div>
                <div style={{ fontSize: '13px', opacity: 0.8 }}>Average Workdays on Leave</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: 4 }}>
                  {macroInsights.avgBalance.toFixed(1)}
                </div>
                <div style={{ fontSize: '13px', opacity: 0.8 }}>Average Balance (days)</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: '700', 
                  marginBottom: 4,
                  color: macroInsights.negativeBalanceCount > 0 ? '#fbbf24' : '#10b981'
                }}>
                  {macroInsights.negativeBalanceCount}
                </div>
                <div style={{ fontSize: '13px', opacity: 0.8 }}>Negative Balances</div>
              </div>
            </div>
          </div>

          {/* Insights Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
            <InsightCard
              title="Highest Time Off Usage"
              icon=""
              employees={macroInsights.highestUtilization}
              metric={(emp) => `${emp.utilization_pct}%`}
              color="#dc2626"
              bgColor="#fef2f2"
              isWarning={true}
            />
            
            <InsightCard
              title="Lowest Time Off Usage" 
              icon=""
              employees={macroInsights.lowestUtilization}
              metric={(emp) => `${emp.utilization_pct || 0}%`}
              color="#059669"
              bgColor="#f0fdf4"
            />
            
            <InsightCard
              title="Most Negative Balance"
              icon=""
              employees={macroInsights.mostNegativeBalance}
              metric={(emp) => `${emp.balance.remaining.toFixed(1)} days`}
              color="#dc2626"
              bgColor="#fef2f2"
              isWarning={true}
            />
            
            <InsightCard
              title="Highest Unused Balance"
              icon=""
              employees={macroInsights.highestPositiveBalance}
              metric={(emp) => `${emp.balance.remaining.toFixed(1)} days`}
              color="#7c3aed"
              bgColor="#faf5ff"
            />
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div style={{ 
        background: 'var(--symbolica-card)',
        border: '1px solid var(--symbolica-warm-border)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by employee name, email, or title..."
            style={{
              flex: 1,
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              background: '#F6F4F0'
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              style={{
                background: '#F0EDE6',
                color: 'var(--symbolica-muted)',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              Clear
            </button>
          )}
        </div>
        {searchTerm && (
          <div style={{ marginTop: 12, fontSize: '14px', color: 'var(--symbolica-muted)' }}>
                            Showing {filteredReports.length} of {reports.length} employees
          </div>
        )}
      </div>

      {loading ? (
        <div>
          {/* Loading Header */}
          <div style={{ 
            background: '#EFECE0',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '32px',
            color: 'var(--symbolica-black)'
          }}>
            <h1 className="content-title" style={{ margin: '0 0 8px 0' }}>
              Time-Off Reports Dashboard
            </h1>
            <p style={{ fontSize: '1rem', opacity: 0.9, margin: 0 }}>
              Loading comprehensive team insights...
            </p>
          </div>

          {/* Loading Executive Summary Skeleton */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{
              background: '#F0EDE6',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,253,249,0.3), transparent)',
                animation: 'shimmer 2s infinite'
              }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ 
                  width: '200px', 
                  height: '20px', 
                  background: 'var(--symbolica-warm-border)', 
                  borderRadius: '4px' 
                }} />
                <div style={{ 
                  width: '120px', 
                  height: '16px', 
                  background: 'var(--symbolica-warm-border)', 
                  borderRadius: '4px' 
                }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 16 }}>
                {[1, 2, 3].map(i => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <div style={{
                      width: '60px',
                      height: '28px',
                      background: 'var(--symbolica-warm-border)',
                      borderRadius: '4px',
                      margin: '0 auto 8px'
                    }} />
                    <div style={{ 
                      width: '100px', 
                      height: '14px', 
                      background: 'var(--symbolica-warm-border)', 
                      borderRadius: '4px',
                      margin: '0 auto'
                    }} />
                  </div>
                ))}
              </div>
            </div>

            {/* Loading Insight Cards Skeleton */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
              {[1, 2, 3, 4].map(i => (
                <div key={i} style={{
                  background: 'var(--symbolica-card)',
                  border: '1px solid var(--symbolica-warm-border)',
                  borderRadius: '12px',
                  padding: '16px',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,253,249,0.5), transparent)',
                    animation: 'shimmer 2s infinite',
                    animationDelay: `${i * 0.2}s`
                  }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <div style={{ 
                      width: '24px', 
                      height: '24px', 
                      background: 'var(--symbolica-warm-border)', 
                      borderRadius: '50%' 
                    }} />
                    <div style={{ 
                      width: '140px', 
                      height: '16px', 
                      background: 'var(--symbolica-warm-border)', 
                      borderRadius: '4px' 
                    }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[1, 2, 3].map(j => (
                      <div key={j} style={{
                        padding: '8px',
                        background: '#F6F4F0',
                        borderRadius: '6px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <div style={{ 
                            width: '120px', 
                            height: '14px', 
                            background: 'var(--symbolica-warm-border)', 
                            borderRadius: '4px',
                            marginBottom: '4px'
                          }} />
                          <div style={{ 
                            width: '80px', 
                            height: '12px', 
                            background: 'var(--symbolica-warm-border)', 
                            borderRadius: '4px'
                          }} />
                        </div>
                        <div style={{ 
                          width: '40px', 
                          height: '16px', 
                          background: 'var(--symbolica-warm-border)', 
                          borderRadius: '4px'
                        }} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Loading Status with Animation */}
          <div style={{ 
            background: '#F6F4F0',
            border: '1px solid var(--symbolica-warm-border)',
            borderRadius: '12px',
            padding: '24px',
            textAlign: 'center',
            marginBottom: '32px'
          }}>
            <div style={{ 
              fontSize: '48px', 
              marginBottom: '16px',
              animation: 'pulse 2s infinite'
            }}>
              
            </div>
            <h3 style={{ 
              fontSize: '1.25rem', 
              fontWeight: '600', 
              color: 'var(--symbolica-black)',
              margin: '0 0 8px 0'
            }}>
              Analyzing Team PTO Data
            </h3>
            <p style={{ 
              color: 'var(--symbolica-muted)', 
              fontSize: '14px',
              margin: '0 0 16px 0'
            }}>
              Processing employee balances, calculating insights, and generating reports...
            </p>
            
            {/* Progress Steps */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: 16,
              marginBottom: 16
            }}>
              {[
                { icon: '', label: 'Loading Employees' },
                { icon: '', label: 'Calculating Balances' },
                { icon: '', label: 'Generating Insights' },
                { icon: '', label: 'Finishing Up' }
              ].map((step, index) => (
                <div key={index} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4
                }}>
                  <div style={{
                    fontSize: '20px',
                    animation: 'bounce 1s infinite',
                    animationDelay: `${index * 0.3}s`
                  }}>
                    {step.icon}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: 'var(--symbolica-muted)',
                    fontWeight: '500'
                  }}>
                    {step.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Tips while loading */}
            <div style={{
              background: '#ffffff',
              border: '1px solid #bae6fd',
              borderRadius: '8px',
              padding: '12px',
              fontSize: '13px',
              color: 'var(--symbolica-black)'
            }}>
               <strong>Did you know?</strong> This dashboard processes complex calculations including utilization rates
              and company-wide insights to give you actionable PTO intelligence.
            </div>
          </div>

          {/* Loading Individual Reports Skeleton */}
          <div style={{ 
            background: 'var(--symbolica-card)',
            border: '1px solid var(--symbolica-warm-border)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ fontSize: '18px' }}></div>
              <div style={{ 
                flex: 1,
                height: '40px',
                background: '#F0EDE6',
                borderRadius: '8px',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,253,249,0.5), transparent)',
                  animation: 'shimmer 2s infinite'
                }} />
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{
                background: 'var(--symbolica-card)',
                border: '1px solid var(--symbolica-warm-border)',
                borderRadius: '16px',
                overflow: 'hidden',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,253,249,0.3), transparent)',
                  animation: 'shimmer 2s infinite',
                  animationDelay: `${i * 0.3}s`
                }} />
                
                {/* Header skeleton */}
                <div style={{ 
                  background: '#F6F4F0',
                  padding: '20px',
                  borderBottom: '1px solid var(--symbolica-warm-border)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <div style={{ 
                      width: '40px', 
                      height: '40px', 
                      borderRadius: '50%', 
                      background: 'var(--symbolica-warm-border)'
                    }} />
                    <div>
                      <div style={{ 
                        width: '120px', 
                        height: '18px', 
                        background: 'var(--symbolica-warm-border)', 
                        borderRadius: '4px',
                        marginBottom: '4px'
                      }} />
                      <div style={{ 
                        width: '100px', 
                        height: '14px', 
                        background: 'var(--symbolica-warm-border)', 
                        borderRadius: '4px'
                      }} />
                    </div>
                  </div>
                </div>

                {/* Content skeleton */}
                <div style={{ padding: '20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 20 }}>
                    {[1, 2, 3, 4].map(j => (
                      <div key={j} style={{
                        background: '#F6F4F0',
                        border: '1px solid var(--symbolica-warm-border)',
                        borderRadius: '8px',
                        padding: '12px'
                      }}>
                        <div style={{ 
                          width: '20px', 
                          height: '20px', 
                          background: 'var(--symbolica-warm-border)', 
                          borderRadius: '4px',
                          marginBottom: '8px'
                        }} />
                        <div style={{ 
                          width: '40px', 
                          height: '20px', 
                          background: 'var(--symbolica-warm-border)', 
                          borderRadius: '4px',
                          marginBottom: '4px'
                        }} />
                        <div style={{ 
                          width: '60px', 
                          height: '12px', 
                          background: 'var(--symbolica-warm-border)', 
                          borderRadius: '4px'
                        }} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <style>
            {`
              @keyframes shimmer {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(100%); }
              }
              @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.5; }
              }
              @keyframes bounce {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-10px); }
              }
            `}
          </style>
        </div>
      ) : filteredReports.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '64px',
          background: 'var(--symbolica-card)',
          borderRadius: '12px',
          border: '1px solid var(--symbolica-warm-border)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                              {searchTerm ? '' : ''}
          </div>
          <div style={{ color: 'var(--symbolica-muted)', fontSize: '18px', fontWeight: '500' }}>
            {searchTerm ? 'No employees match your search.' : 'No PTO reports found.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
          {filteredReports.map(rep => {
            const name = getEmployeeDisplayName({ 
              first_name: rep.first_name, 
              last_name: rep.last_name, 
              email: rep.email 
            });
            const bal = rep.balance || {};
            const utilizationColor = getUtilizationColor(rep.utilization_pct);
            
            return (
              <div key={rep.email} style={{ 
                background: 'var(--symbolica-card)',
                border: '1px solid var(--symbolica-warm-border)',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}>
                
                {/* Header */}
                <div style={{ 
                  background: '#F6F4F0',
                  padding: '20px',
                  borderBottom: '1px solid var(--symbolica-warm-border)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <div style={{ 
                      width: '40px', 
                      height: '40px', 
                      borderRadius: '50%', 
                      background: 'var(--symbolica-black)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--symbolica-black)',
                      fontSize: '16px',
                      fontWeight: '600'
                    }}>
                      {`${(rep.first_name || '').charAt(0)}${(rep.last_name || '').charAt(0)}`.toUpperCase()}
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600', color: 'var(--symbolica-black)' }}>
                        {name}
                      </h3>
                      <div style={{ fontSize: '13px', color: 'var(--symbolica-muted)' }}>
                        {rep.title || '—'} • {rep.country || '—'}
                      </div>
                    </div>
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: 'var(--symbolica-muted)',
                    background: 'var(--symbolica-card)',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    display: 'inline-block'
                  }}>
                    Started: {formatDate(rep.start_date) || '—'}
                  </div>
                </div>

                {/* Metrics Grid */}
                <div style={{ padding: '20px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: 12, marginBottom: 20 }}>
                    <MetricCard
                      icon="🏖️"
                      label="PTO Used"
                      value={bal.used || '0'}
                      color="#0891b2"
                      subtitle="this cycle"
                    />
                    <MetricCard
                      icon="📅"
                      label="Future Approved"
                      value={bal.future_approved || '0'}
                      color="#7c3aed"
                    />
                    <MetricCard
                      icon="⏳"
                      label="Pending"
                      value={bal.pending || '0'}
                      color="#ea580c"
                      subtitle="awaiting approval"
                    />
                  </div>
                  
                  {/* Sick Time Summary */}
                  {rep.sick_balance && (
                    <div style={{
                      background: '#F6F4F0',
                      border: '1px solid #bae6fd',
                      borderRadius: '12px',
                      padding: '16px',
                      marginBottom: 20
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 8, 
                        marginBottom: 12,
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'var(--symbolica-black)'
                      }}>
                        <span>🏥</span>
                        Sick Time Overview
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: rep.sick_balance.is_usage_only ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: 12 }}>
                        {!rep.sick_balance.is_usage_only && (
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--symbolica-black)' }}>
                              {rep.sick_balance.remaining || '0'}
                            </div>
                            <div style={{ fontSize: '11px', color: '#0369a1', fontWeight: '500' }}>
                              Available
                            </div>
                          </div>
                        )}
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--symbolica-black)' }}>
                            {rep.sick_balance.used || '0'}
                          </div>
                          <div style={{ fontSize: '11px', color: '#0369a1', fontWeight: '500' }}>
                            Used
                          </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--symbolica-black)' }}>
                            {rep.sick_usage?.requests?.length || '0'}
                          </div>
                          <div style={{ fontSize: '11px', color: '#0369a1', fontWeight: '500' }}>
                            Requests
                          </div>
                        </div>
                      </div>
                      {rep.sick_balance.is_usage_only && (
                        <div style={{
                          marginTop: '8px',
                          fontSize: '11px',
                          color: '#0369a1',
                          fontStyle: 'italic',
                          textAlign: 'center'
                        }}>
                          Usage-only policy (no accrual)
                        </div>
                      )}
                    </div>
                  )}

                  {/* Current Balance */}
                  <div style={{
                    background: 'var(--symbolica-accent)',
                    borderRadius: '10px',
                    padding: '16px',
                    color: 'var(--symbolica-black)',
                    textAlign: 'center',
                    marginBottom: 20,
                    cursor: 'default'
                  }}>
                    <div style={{ fontSize: '13px', opacity: 0.8, marginBottom: 4 }}>
                      Remaining Balance
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: '700' }}>
                      {bal.remaining || '0'} days
                    </div>
                    <div style={{ fontSize: '12px', opacity: 0.7, marginTop: 4 }}>
                      {bal.total} allowance &minus; {bal.used} used
                    </div>
                    {bal.allow_full_year_upfront && bal.accrued_to_date != null && bal.accrued_to_date !== bal.total && (
                      <div style={{ marginTop: '8px', fontSize: '13px', opacity: 0.85 }}>
                        Accrued to Date: {bal.accrued_to_date} days
                      </div>
                    )}
                    {(bal.allow_full_year_upfront || bal.has_calendar_year || bal.has_anniversary_cycle) && (
                      <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                        {bal.allow_full_year_upfront && (
                          <div 
                            onMouseEnter={() => setHoveredBadge(rep.email)}
                            onMouseLeave={() => setHoveredBadge(null)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              background: 'rgba(0, 0, 0, 0.05)',
                              border: '1px solid rgba(0, 0, 0, 0.1)',
                              borderRadius: '12px',
                              padding: '4px 10px',
                              fontSize: '11px',
                              fontWeight: '600',
                              cursor: 'help',
                              position: 'relative'
                            }}>
                            
                            <span>Advance booking</span>
                            {hoveredBadge === rep.email && (
                              <div style={{
                                position: 'absolute',
                                bottom: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                marginBottom: '8px',
                                background: 'rgba(0, 0, 0, 0.9)',
                                color: 'var(--symbolica-black)',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 'normal',
                                whiteSpace: 'normal',
                                width: '250px',
                                textAlign: 'center',
                                zIndex: 1000,
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                              }}>
                                This employee can book against their full annual allowance upfront. "Accrued to Date" shows what they have actually earned pro-rata. Using more than accrued creates a liability that must be reconciled at year-end or termination.
                                <div style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  width: 0,
                                  height: 0,
                                  borderLeft: '6px solid transparent',
                                  borderRight: '6px solid transparent',
                                  borderTop: '6px solid rgba(0, 0, 0, 0.9)'
                                }} />
                              </div>
                            )}
                          </div>
                        )}
                        {bal.has_calendar_year && (
                          <div 
                            onMouseEnter={() => setHoveredCalendarBadge(rep.email)}
                            onMouseLeave={() => setHoveredCalendarBadge(null)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              background: 'rgba(0, 0, 0, 0.05)',
                              border: '1px solid rgba(0, 0, 0, 0.1)',
                              borderRadius: '12px',
                              padding: '4px 10px',
                              fontSize: '11px',
                              fontWeight: '600',
                              cursor: 'help',
                              position: 'relative'
                            }}>
                            
                            <span>Calendar year</span>
                            {hoveredCalendarBadge === rep.email && (
                              <div style={{
                                position: 'absolute',
                                bottom: '100%',
                                right: '-10px',
                                marginBottom: '8px',
                                background: 'rgba(0, 0, 0, 0.9)',
                                color: 'var(--symbolica-black)',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 'normal',
                                whiteSpace: 'normal',
                                width: '280px',
                                maxWidth: 'calc(100vw - 40px)',
                                textAlign: 'center',
                                zIndex: 1000,
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                              }}>
                                This employee's PTO follows a calendar year schedule (Jan 1 - Dec 31). Unlike anniversary-based policies, they receive their new annual allocation on January 1st, regardless of their start date. If they started mid-year, their first year was pro-rated. Each December 31st, {bal.calendar_year_carryover_info ? `${bal.calendar_year_carryover_info}` : 'unused days expire'}.
                                <div style={{
                                  position: 'absolute',
                                  top: '100%',
                                  right: '30px',
                                  width: 0,
                                  height: 0,
                                  borderLeft: '6px solid transparent',
                                  borderRight: '6px solid transparent',
                                  borderTop: '6px solid rgba(0, 0, 0, 0.9)'
                                }} />
                              </div>
                            )}
                          </div>
                        )}
                        {bal.has_anniversary_cycle && (
                          <div 
                            onMouseEnter={() => setHoveredAnniversaryBadge(rep.email)}
                            onMouseLeave={() => setHoveredAnniversaryBadge(null)}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              background: 'rgba(0, 0, 0, 0.05)',
                              border: '1px solid rgba(0, 0, 0, 0.1)',
                              borderRadius: '12px',
                              padding: '4px 10px',
                              fontSize: '11px',
                              fontWeight: '600',
                              cursor: 'help',
                              position: 'relative'
                            }}>
                            
                            <span>Anniversary cycle</span>
                            {hoveredAnniversaryBadge === rep.email && (
                              <div style={{
                                position: 'absolute',
                                bottom: '100%',
                                right: '-10px',
                                marginBottom: '8px',
                                background: 'rgba(0, 0, 0, 0.9)',
                                color: 'var(--symbolica-black)',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 'normal',
                                whiteSpace: 'normal',
                                width: '280px',
                                maxWidth: 'calc(100vw - 40px)',
                                textAlign: 'center',
                                zIndex: 1000,
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                              }}>
                                This employee's PTO follows their work anniversary schedule. They receive their annual allocation on their start date anniversary each year. This is personalized to each employee - different from calendar year policies where everyone gets new PTO on January 1st. On their anniversary date, {bal.anniversary_carryover_info ? `${bal.anniversary_carryover_info}` : 'unused days expire'}.
                                <div style={{
                                  position: 'absolute',
                                  top: '100%',
                                  right: '30px',
                                  width: 0,
                                  height: 0,
                                  borderLeft: '6px solid transparent',
                                  borderRight: '6px solid transparent',
                                  borderTop: '6px solid rgba(0, 0, 0, 0.9)'
                                }} />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Utilization */}
                  {rep.utilization_pct !== null && (
                    <div style={{
                      background: '#F6F4F0',
                      border: '1px solid var(--symbolica-warm-border)',
                      borderRadius: '10px',
                      padding: '12px',
                      marginBottom: 20
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: '13px', fontWeight: '500', color: 'var(--symbolica-black)' }}>
                          Workdays on Non-Sick Leave
                        </span>
                        <span style={{ fontSize: '16px', fontWeight: '700', color: utilizationColor }}>
                          {rep.utilization_pct}%
                        </span>
                      </div>
                      <ProgressBar 
                        value={rep.utilization_pct} 
                        max={100} 
                        color={utilizationColor} 
                        height={8}
                      />
                    </div>
                  )}

                  {/* Upcoming Requests */}
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 8, 
                      marginBottom: 12,
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--symbolica-black)'
                    }}>
                      <span></span>
                      Upcoming Time Off
                      <span style={{
                        background: '#dbeafe',
                        color: '#1e40af',
                        padding: '2px 6px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>
                        {(rep.upcoming || []).length}
                      </span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {(rep.upcoming || []).length === 0 ? (
                        <div style={{ 
                          textAlign: 'center', 
                          padding: '16px',
                          color: 'var(--symbolica-muted)',
                          fontSize: '13px',
                          fontStyle: 'italic',
                          background: '#F6F4F0',
                          borderRadius: '8px'
                        }}>
                          No upcoming time off scheduled
                        </div>
                      ) : (
                        (rep.upcoming || []).slice(0, 3).map(u => (
                          <div key={u.id} style={{
                            background: '#f0f9ff',
                            border: '1px solid #bae6fd',
                            borderRadius: '8px',
                            padding: '10px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: '500', color: '#0369a1' }}>
                                {formatDate(u.start_date || u.startDate)} → {formatDate(u.end_date || u.endDate)}
                              </div>
                              <div style={{ fontSize: '12px', color: 'var(--symbolica-muted)' }}>
                                {u.days} day{u.days !== 1 ? 's' : ''}
                              </div>
                            </div>
                            <span style={getStatusBadge(u.status)}>
                              {u.status}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Recent History */}
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      marginBottom: 12
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'var(--symbolica-black)'
                      }}>
                        <span></span>
                        Recent History
                        <span style={{
                          background: '#f3e8ff',
                          color: '#7c2d12',
                          padding: '2px 6px',
                          borderRadius: '10px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          {(rep.history || []).length}
                        </span>
                      </div>
                      {(rep.history || []).length > 3 && (
                        <button 
                          onClick={() => openHistoryModal(rep)}
                          style={{ 
                            background: 'none', 
                            border: 'none', 
                            color: 'var(--symbolica-black)', 
                            cursor: 'pointer', 
                            fontSize: '12px', 
                            fontWeight: '500',
                            textDecoration: 'underline',
                            padding: '4px 0'
                          }}
                        >
                          View All ({(rep.history || []).length})
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {(rep.history || []).length === 0 ? (
                        <div style={{ 
                          textAlign: 'center', 
                          padding: '16px',
                          color: 'var(--symbolica-muted)',
                          fontSize: '13px',
                          fontStyle: 'italic',
                          background: '#F6F4F0',
                          borderRadius: '8px'
                        }}>
                          No historical requests
                        </div>
                      ) : (
                        (rep.history || []).slice(0, 3).map(h => (
                          <div key={h.id} style={{
                            background: '#fefce8',
                            border: '1px solid #fde047',
                            borderRadius: '6px',
                            padding: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div>
                              <div style={{ fontSize: '12px', fontWeight: '500', color: '#a16207' }}>
                                {formatDate(h.start_date || h.startDate)} → {formatDate(h.end_date || h.endDate)}
                              </div>
                              <div style={{ fontSize: '11px', color: '#ca8a04' }}>
                                {h.days} day{h.days !== 1 ? 's' : ''}
                              </div>
                            </div>
                            <span style={getStatusBadge(h.status)}>
                              {h.status}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Calculation Breakdown Link */}
                  <div style={{ 
                    marginTop: '20px', 
                    paddingTop: '16px',
                    borderTop: '1px solid var(--symbolica-warm-border)',
                    textAlign: 'center'
                  }}>
                    <button 
                      onClick={() => openCalculationModal(rep)}
                      style={{ 
                        background: 'var(--symbolica-accent)',
                        color: 'var(--symbolica-black)',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.3)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.2)';
                      }}
                    >
                       View Calculation Breakdown
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer with Database Export Link */}
      <div style={{
        borderTop: '1px solid var(--symbolica-warm-border)',
        paddingTop: '20px',
        marginTop: '40px',
        textAlign: 'center'
      }}>
        <button
          onClick={handleExportDatabase}
          disabled={exportLoading}
          style={{
            background: 'none',
            border: 'none',
            color: exportLoading ? 'var(--symbolica-muted)' : 'var(--symbolica-muted)',
            fontSize: '13px',
            cursor: exportLoading ? 'not-allowed' : 'pointer',
            textDecoration: 'underline',
            padding: '4px 8px',
            transition: 'color 0.2s ease'
          }}
          onMouseOver={(e) => {
            if (!exportLoading) {
              e.target.style.color = 'var(--symbolica-black)';
            }
          }}
          onMouseOut={(e) => {
            if (!exportLoading) {
              e.target.style.color = 'var(--symbolica-muted)';
            }
          }}
        >
          {exportLoading ? 'Exporting database...' : 'Export database to JSON files'}
        </button>
      </div>

      {/* Enhanced History Modal */}
      {historyModalOpen && selectedEmployeeHistory && (
        <Modal isOpen={historyModalOpen} onClose={() => setHistoryModalOpen(false)}>
          <div style={{ 
            background: '#EFECE0',
            margin: '-24px -24px 24px -24px',
            padding: '24px',
            color: 'var(--symbolica-black)'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.5rem', fontWeight: '700' }}>
              Complete PTO History
            </h3>
            <div style={{ fontSize: '1rem', opacity: 0.9 }}>
              {selectedEmployeeHistory.first_name} {selectedEmployeeHistory.last_name}
            </div>
            <div style={{ fontSize: '0.875rem', opacity: 0.8, marginTop: 4 }}>
              {selectedEmployeeHistory.title || '—'} • {selectedEmployeeHistory.country || '—'}
            </div>
          </div>
          
          {(selectedEmployeeHistory.history || []).length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '48px',
              color: 'var(--symbolica-muted)',
              background: '#F6F4F0',
              borderRadius: '12px'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}></div>
              <div style={{ fontSize: '16px', fontWeight: '500' }}>
                No historical PTO requests found
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '400px', overflowY: 'auto' }}>
              {(selectedEmployeeHistory.history || []).map(h => (
                <div key={h.id} style={{ 
                  border: '1px solid var(--symbolica-warm-border)', 
                  borderRadius: '12px', 
                  padding: '16px',
                  background: 'var(--symbolica-card)',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--symbolica-black)' }}>
                        {formatDate(h.start_date || h.startDate)} → {formatDate(h.end_date || h.endDate)}
                      </div>
                      <div style={{ fontSize: '14px', color: 'var(--symbolica-muted)', marginTop: 2 }}>
                        {h.days} day{h.days === 1 ? '' : 's'}
                      </div>
                    </div>
                    <span style={getStatusBadge(h.status)}>
                      {h.status}
                    </span>
                  </div>
                  {h.reason && (
                    <div style={{ 
                      background: '#F6F4F0',
                      border: '1px solid var(--symbolica-warm-border)',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      fontSize: '13px',
                      color: '#64748b',
                      marginTop: 8
                    }}>
                                              {h.reason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
            <button 
              className="primary-button" 
              onClick={() => setHistoryModalOpen(false)}
              style={{
                background: '#EFECE0',
                border: 'none',
                color: 'var(--symbolica-black)',
                padding: '10px 20px',
                borderRadius: '8px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </Modal>
      )}

      {/* Calculation Breakdown Modal */}
      {calculationModalOpen && selectedEmployeeCalculation && (
        <Modal isOpen={calculationModalOpen} onClose={() => setCalculationModalOpen(false)}>
          <div style={{ 
            background: 'var(--symbolica-accent)',
            margin: '-24px -24px 24px -24px',
            padding: '24px',
            color: 'var(--symbolica-black)'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '1.5rem', fontWeight: '700' }}>
               Time-Off Calculation Breakdown
            </h3>
            <div style={{ fontSize: '1rem', opacity: 0.9 }}>
              {selectedEmployeeCalculation.first_name} {selectedEmployeeCalculation.last_name}
            </div>
            <div style={{ fontSize: '0.875rem', opacity: 0.8, marginTop: 4 }}>
              {selectedEmployeeCalculation.title || '—'} • {selectedEmployeeCalculation.country || '—'}
            </div>
          </div>
          
          {calculationLoading ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '48px',
              color: 'var(--symbolica-muted)'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
              <div style={{ fontSize: '16px', fontWeight: '500' }}>
                Loading calculation breakdown...
              </div>
            </div>
          ) : calculationData ? (
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {/* Employee Summary */}
              <div style={{
                background: '#F6F4F0',
                border: '1px solid var(--symbolica-warm-border)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '1.1rem', fontWeight: '600', color: 'var(--symbolica-black)' }}>
                  Employee Summary
                </h4>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                  gap: '12px',
                  fontSize: '14px'
                }}>
                  <div>
                    <span style={{ color: 'var(--symbolica-muted)', fontWeight: '500' }}>Start Date:</span>
                    <div style={{ fontWeight: '600', color: 'var(--symbolica-black)' }}>
                      {formatDate(calculationData.employee.start_date)}
                    </div>
                  </div>
                  <div>
                    <span style={{ color: 'var(--symbolica-muted)', fontWeight: '500' }}>Tenure:</span>
                    <div style={{ fontWeight: '600', color: 'var(--symbolica-black)' }}>
                      {calculationData.employee.tenure_years} years ({calculationData.employee.tenure_days} days)
                    </div>
                  </div>
                  <div>
                    <span style={{ color: 'var(--symbolica-muted)', fontWeight: '500' }}>Final Balance:</span>
                    <div style={{ fontWeight: '700', color: '#059669', fontSize: '16px' }}>
                      {calculationData.computed_balance?.remaining || 0} days
                    </div>
                  </div>
                </div>
              </div>

              {/* Sick Time Summary */}
              {selectedEmployeeCalculation.sick_balance && (
                <div style={{
                  background: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '1.1rem', fontWeight: '600', color: 'var(--symbolica-black)' }}>
                    🏥 Sick Time Summary
                  </h4>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: selectedEmployeeCalculation.sick_balance.is_usage_only 
                      ? 'repeat(2, 1fr)' 
                      : 'repeat(auto-fit, minmax(150px, 1fr))', 
                    gap: '12px',
                    fontSize: '14px'
                  }}>
                    {!selectedEmployeeCalculation.sick_balance.is_usage_only && (
                      <>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ color: '#0369a1', fontSize: '12px', marginBottom: '4px' }}>Total Available</div>
                          <div style={{ fontWeight: '700', color: 'var(--symbolica-black)', fontSize: '18px' }}>
                            {selectedEmployeeCalculation.sick_balance.total || 0} days
                          </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ color: '#0369a1', fontSize: '12px', marginBottom: '4px' }}>Remaining</div>
                          <div style={{ fontWeight: '700', color: '#059669', fontSize: '18px' }}>
                            {selectedEmployeeCalculation.sick_balance.remaining || 0} days
                          </div>
                        </div>
                      </>
                    )}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#0369a1', fontSize: '12px', marginBottom: '4px' }}>Used</div>
                      <div style={{ fontWeight: '700', color: '#dc2626', fontSize: '18px' }}>
                        {selectedEmployeeCalculation.sick_balance.used || 0} days
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#0369a1', fontSize: '12px', marginBottom: '4px' }}>Pending</div>
                      <div style={{ fontWeight: '700', color: '#ea580c', fontSize: '18px' }}>
                        {selectedEmployeeCalculation.sick_balance.pending || 0} days
                      </div>
                    </div>
                  </div>
                  
                  {/* Sick Time Requests */}
                  {selectedEmployeeCalculation.sick_usage?.requests && selectedEmployeeCalculation.sick_usage.requests.length > 0 && (
                    <div style={{ marginTop: '16px' }}>
                      <h5 style={{ margin: '0 0 8px 0', fontSize: '0.9rem', fontWeight: '600', color: 'var(--symbolica-black)' }}>
                        Recent Sick Time Requests ({selectedEmployeeCalculation.sick_usage.requests.length} total)
                      </h5>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '120px', overflowY: 'auto' }}>
                        {selectedEmployeeCalculation.sick_usage.requests.slice(0, 5).map((req, index) => (
                          <div key={index} style={{
                            background: '#ffffff',
                            border: '1px solid #e0f2fe',
                            borderRadius: '6px',
                            padding: '8px',
                            fontSize: '12px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div>
                              <div style={{ fontWeight: '600', color: 'var(--symbolica-black)' }}>
                                {formatDate(req.start_date)} → {formatDate(req.end_date)}
                              </div>
                              {req.reason && (
                                <div style={{ color: '#0369a1', fontSize: '11px', marginTop: '2px' }}>
                                  {req.reason.length > 40 ? `${req.reason.substring(0, 40)}...` : req.reason}
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: '600', color: 'var(--symbolica-black)' }}>
                                {req.days} day{req.days !== 1 ? 's' : ''}
                              </span>
                              <span style={getStatusBadge(req.status)}>
                                {req.status}
                              </span>
                            </div>
                          </div>
                        ))}
                        {selectedEmployeeCalculation.sick_usage.requests.length > 5 && (
                          <div style={{ 
                            textAlign: 'center', 
                            fontSize: '11px', 
                            color: '#0369a1',
                            fontStyle: 'italic',
                            padding: '4px'
                          }}>
                            ...and {selectedEmployeeCalculation.sick_usage.requests.length - 5} more requests
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {selectedEmployeeCalculation.sick_balance.is_usage_only && (
                    <div style={{
                      marginTop: '12px',
                      padding: '8px',
                      background: '#dbeafe',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#1e40af',
                      fontWeight: '500'
                    }}>
                      ℹ️ Usage-only policy: No accrual - fixed allowance per period
                    </div>
                  )}
                </div>
              )}

              {/* PTO Policy Breakdown */}
              <div style={{
                marginBottom: '16px',
                fontSize: '1.2rem',
                fontWeight: '700',
                color: 'var(--symbolica-black)',
                borderBottom: '2px solid var(--symbolica-warm-border)',
                paddingBottom: '8px'
              }}>
                🏖️ PTO Policy Breakdown
              </div>
              {(calculationData.policies || []).map((policy, index) => (
                <div key={index} style={{
                  border: '1px solid var(--symbolica-warm-border)',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '16px',
                  background: 'var(--symbolica-card)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h5 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: 'var(--symbolica-black)' }}>
                      {policy.policy_name}
                    </h5>
                    <div style={{ 
                      background: policy.was_capped ? '#fee2e2' : '#dcfce7',
                      color: policy.was_capped ? '#dc2626' : '#059669',
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {policy.was_capped ? 'Capped' : 'Uncapped'}
                    </div>
                  </div>

                  {/* Final Totals */}
                  <div style={{
                    background: '#F6F4F0',
                    border: '1px solid var(--symbolica-warm-border)',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '16px'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', fontSize: '14px' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: 'var(--symbolica-muted)', fontSize: '12px', marginBottom: '4px' }}>Total Accrued</div>
                        <div style={{ fontWeight: '700', color: '#059669', fontSize: '16px' }}>
                          {policy.total_accrued_before_cap} days
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: 'var(--symbolica-muted)', fontSize: '12px', marginBottom: '4px' }}>After Cap</div>
                        <div style={{ fontWeight: '700', color: 'var(--symbolica-black)', fontSize: '16px' }}>
                          {policy.total_accrued_after_cap} days
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ color: 'var(--symbolica-muted)', fontSize: '12px', marginBottom: '4px' }}>Max Balance</div>
                        <div style={{ fontWeight: '600', color: 'var(--symbolica-muted)', fontSize: '14px' }}>
                          {policy.max_balance > 0 ? `${policy.max_balance} days` : 'No limit'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Year-by-Year Breakdown */}
                  <div>
                    <h6 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', fontWeight: '600', color: 'var(--symbolica-muted)' }}>
                      Year-by-Year Accrual & Usage Breakdown
                    </h6>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {(policy.yearly_breakdown || []).map((year, yearIndex) => {
                        const endBalance = year.year_end_balance || 0;
                        const isNegative = endBalance < 0;
                        const carriedForward = year.carried_forward || 0;
                        const usedThisPeriod = year.used_this_period || 0;
                        
                        return (
                          <div key={yearIndex} style={{
                            background: isNegative ? '#fef2f2' : '#F6F4F0',
                            border: isNegative ? '1px solid #fecaca' : '1px solid var(--symbolica-warm-border)',
                            borderRadius: '8px',
                            padding: '12px'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--symbolica-black)' }}>
                                {formatDate(year.year_start)} → {formatDate(year.year_end)}
                              </div>
                              <div style={{ 
                                fontSize: '14px', 
                                fontWeight: '700', 
                                color: isNegative ? '#dc2626' : '#059669',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                {isNegative && <span style={{ fontSize: '12px' }}>⚠️</span>}
                                Balance: {endBalance} days
                              </div>
                            </div>
                            
                            {/* Accrual and Usage Summary */}
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: 'repeat(3, 1fr)', 
                              gap: '8px', 
                              marginBottom: '8px',
                              padding: '8px',
                              background: '#ffffff',
                              borderRadius: '6px',
                              fontSize: '12px'
                            }}>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ color: 'var(--symbolica-muted)', fontSize: '11px' }}>Accrued</div>
                                <div style={{ fontWeight: '600', color: '#059669' }}>+{year.accrued_this_period}</div>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ color: 'var(--symbolica-muted)', fontSize: '11px' }}>Used</div>
                                <div style={{ fontWeight: '600', color: usedThisPeriod > 0 ? '#dc2626' : 'var(--symbolica-muted)' }}>
                                  {usedThisPeriod > 0 ? `-${usedThisPeriod}` : '0'}
                                </div>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <div style={{ color: 'var(--symbolica-muted)', fontSize: '11px' }}>Carried Forward</div>
                                <div style={{ fontWeight: '600', color: carriedForward < 0 ? '#dc2626' : 'var(--symbolica-muted)' }}>
                                  {carriedForward !== 0 ? (carriedForward > 0 ? `+${carriedForward}` : `${carriedForward}`) : '0'}
                                </div>
                              </div>
                            </div>
                            
                            {/* Detailed Information */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', fontSize: '12px' }}>
                              <div>
                                <span style={{ color: 'var(--symbolica-muted)' }}>Tenure at start:</span>
                                <span style={{ marginLeft: '4px', fontWeight: '600' }}>{year.tenure_at_start} years</span>
                              </div>
                              <div>
                                <span style={{ color: 'var(--symbolica-muted)' }}>Days per year:</span>
                                <span style={{ marginLeft: '4px', fontWeight: '600' }}>{year.days_per_year}</span>
                              </div>
                              <div>
                                <span style={{ color: 'var(--symbolica-muted)' }}>Fraction of year:</span>
                                <span style={{ marginLeft: '4px', fontWeight: '600' }}>{year.fraction_of_year}</span>
                              </div>
                              <div>
                                <span style={{ color: 'var(--symbolica-muted)' }}>Running total:</span>
                                <span style={{ marginLeft: '4px', fontWeight: '600' }}>{year.total_accrued_so_far} days</span>
                              </div>
                            </div>
                            
                            {/* Negative Balance Warning */}
                            {isNegative && (
                              <div style={{ 
                                marginTop: '8px', 
                                padding: '6px 8px',
                                background: '#fee2e2',
                                borderRadius: '4px',
                                fontSize: '11px',
                                color: '#991b1b'
                              }}>
                                <strong>⚠️ Negative Balance:</strong> Employee used more PTO than accrued this period. 
                                                                 {yearIndex < (policy.yearly_breakdown || []).length - 1 && ' This deficit will be carried forward to the next period.'}
                              </div>
                            )}
                            
                            {/* Tier Information */}
                            {year.active_tier && (
                              <div style={{ 
                                marginTop: '8px', 
                                padding: '6px 8px',
                                background: '#e0e7ff',
                                borderRadius: '4px',
                                fontSize: '11px',
                                color: '#3730a3'
                              }}>
                                <strong>Active Tier:</strong> {year.active_tier.min_years}+ years → {year.active_tier.days_per_year} days/year
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}

              {/* Current Balance Summary */}
              <div style={{
                background: 'var(--symbolica-black)',
                borderRadius: '12px',
                padding: '16px',
                color: 'var(--symbolica-black)',
                marginTop: '16px'
              }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '1.1rem', fontWeight: '600' }}>
                  Current Balance Summary
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', fontSize: '14px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ opacity: 0.8, fontSize: '12px', marginBottom: '4px' }}>Total Allowance</div>
                    <div style={{ fontWeight: '700', fontSize: '16px' }}>
                      {calculationData.computed_balance?.total || 0} days
                    </div>
                  </div>
                  {calculationData.computed_balance?.allow_full_year_upfront && calculationData.computed_balance?.accrued_to_date != null && calculationData.computed_balance?.accrued_to_date !== calculationData.computed_balance?.total && (
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ opacity: 0.8, fontSize: '12px', marginBottom: '4px' }}>Accrued to Date</div>
                      <div style={{ fontWeight: '700', fontSize: '16px' }}>
                        {calculationData.computed_balance.accrued_to_date} days
                      </div>
                    </div>
                  )}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ opacity: 0.8, fontSize: '12px', marginBottom: '4px' }}>Used</div>
                    <div style={{ fontWeight: '700', fontSize: '16px' }}>
                      {calculationData.computed_balance?.used || 0} days
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ opacity: 0.8, fontSize: '12px', marginBottom: '4px' }}>Pending</div>
                    <div style={{ fontWeight: '700', fontSize: '16px' }}>
                      {calculationData.computed_balance?.pending || 0} days
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ opacity: 0.8, fontSize: '12px', marginBottom: '4px' }}>Remaining</div>
                    <div style={{ fontWeight: '700', fontSize: '18px' }}>
                      {calculationData.computed_balance?.remaining || 0} days
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '48px',
              color: 'var(--symbolica-muted)'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
              <div style={{ fontSize: '16px', fontWeight: '500' }}>
                Failed to load calculation breakdown
              </div>
            </div>
          )}
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
            <button 
              className="primary-button" 
              onClick={() => setCalculationModalOpen(false)}
              style={{
                background: 'var(--symbolica-accent)',
                border: 'none',
                color: 'var(--symbolica-black)',
                padding: '10px 20px',
                borderRadius: '8px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </Modal>
      )}
      
      {/* CSS for spinning animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
} 